"use client"

/**
 * Composant de contrôles mobiles pour le mode Self-Test
 * Permet de naviguer et contrôler les panneaux LED depuis un téléphone
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowUp, 
  ArrowDown, 
  ArrowLeft, 
  ArrowRight,
  Zap,
  AlertTriangle,
  Monitor
} from 'lucide-react';

interface MobileSelfTestControlsProps {
  // État du mode Self-Test
  selfTestMode: {
    enabled: boolean;
    selectedPanelX: number;
    selectedPanelY: number;
    trailMode: boolean;
    showBorders: boolean;
    activePanels: Set<string>;
  };
  
  // État des panneaux cyan
  cyanPanels: Set<string>;
  
  // Mode subdivision PCB
  subdivisionMode: boolean;
  selectedQuadrant: 'tl' | 'tr' | 'bl' | 'br';
  redZones: Set<string>;
  
  // Configuration LED Wall
  ledWallConfig?: {
    columns: number;
    rows: number;
  };
  
  // Callbacks pour envoyer les actions au serveur
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right') => void;
  onToggleCyan: () => void;
  onToggleRedZone: () => void;
  onNavigateQuadrant: (quadrant: 'tl' | 'tr' | 'bl' | 'br') => void;
  
  className?: string;
}

export default function MobileSelfTestControls({
  selfTestMode,
  cyanPanels,
  subdivisionMode,
  selectedQuadrant,
  redZones,
  ledWallConfig,
  onNavigate,
  onToggleCyan,
  onToggleRedZone,
  onNavigateQuadrant,
  className
}: MobileSelfTestControlsProps) {

  if (!selfTestMode.enabled || !ledWallConfig) {
    return null;
  }

  const currentPanelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;
  const isPanelCyan = cyanPanels.has(currentPanelKey);
  const currentRedZoneKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY},${selectedQuadrant}`;
  const isCurrentZoneRed = redZones.has(currentRedZoneKey);

  return (
    <Card className={`w-full ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-center flex items-center justify-center gap-2">
          <Monitor className="w-5 h-5" />
          Contrôles Self-Test Mobile
        </CardTitle>
        <CardDescription className="text-center">
          Naviguez et testez les panneaux LED depuis votre téléphone
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Indicateur de panneau actuel */}
        <div className="text-center">
          <Badge variant="outline" className="text-lg px-3 py-1">
            Panneau {selfTestMode.selectedPanelX},{selfTestMode.selectedPanelY}
          </Badge>
          <p className="text-sm text-muted-foreground mt-1">
            Grille: {ledWallConfig.columns} × {ledWallConfig.rows}
          </p>
        </div>

        {/* Contrôles de navigation directionnelle */}
        <div className="grid grid-cols-3 gap-2 w-full max-w-60 mx-auto">
          {/* Ligne du haut */}
          <div></div>
          <Button
            size="lg"
            variant="outline"
            className="aspect-square p-2"
            onClick={() => onNavigate('up')}
            disabled={selfTestMode.selectedPanelY <= 1}
          >
            <ArrowUp className="w-6 h-6" />
          </Button>
          <div></div>

          {/* Ligne du milieu */}
          <Button
            size="lg"
            variant="outline"
            className="aspect-square p-2"
            onClick={() => onNavigate('left')}
            disabled={selfTestMode.selectedPanelX <= 1}
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          
          {/* Bouton central - Identifier panneau en cyan/bleu */}
          <Button
            size="lg"
            variant={isPanelCyan ? "default" : "outline"}
            className={`aspect-square p-2 ${isPanelCyan ? 'bg-cyan-500 hover:bg-cyan-600' : ''}`}
            onClick={onToggleCyan}
          >
            <Zap className="w-6 h-6" />
          </Button>
          
          <Button
            size="lg"
            variant="outline"
            className="aspect-square p-2"
            onClick={() => onNavigate('right')}
            disabled={selfTestMode.selectedPanelX >= ledWallConfig.columns}
          >
            <ArrowRight className="w-6 h-6" />
          </Button>

          {/* Ligne du bas */}
          <div></div>
          <Button
            size="lg"
            variant="outline"
            className="aspect-square p-2"
            onClick={() => onNavigate('down')}
            disabled={selfTestMode.selectedPanelY >= ledWallConfig.rows}
          >
            <ArrowDown className="w-6 h-6" />
          </Button>
          <div></div>
        </div>

        {/* Description des contrôles */}
        <div className="text-center space-y-1">
          <p className="text-sm">
            <Zap className="w-4 h-4 inline mr-1" />
            <span className={isPanelCyan ? "font-semibold text-cyan-600" : ""}>
              {isPanelCyan ? "Panneau identifié" : "Identifier panneau"}
            </span>
          </p>
        </div>

        {/* Contrôles PCB en mode subdivision */}
        {subdivisionMode && isPanelCyan && (
          <>
            <div className="border-t pt-4">
              <h4 className="font-medium text-center mb-3">Mode PCB - Navigation Quadrants</h4>
              
              {/* Navigation quadrants */}
              <div className="grid grid-cols-2 gap-2 max-w-48 mx-auto mb-4">
                <Button
                  size="sm"
                  variant={selectedQuadrant === 'tl' ? "default" : "outline"}
                  onClick={() => onNavigateQuadrant('tl')}
                  className="text-xs"
                >
                  Haut-G
                </Button>
                <Button
                  size="sm"
                  variant={selectedQuadrant === 'tr' ? "default" : "outline"}
                  onClick={() => onNavigateQuadrant('tr')}
                  className="text-xs"
                >
                  Haut-D
                </Button>
                <Button
                  size="sm"
                  variant={selectedQuadrant === 'bl' ? "default" : "outline"}
                  onClick={() => onNavigateQuadrant('bl')}
                  className="text-xs"
                >
                  Bas-G
                </Button>
                <Button
                  size="sm"
                  variant={selectedQuadrant === 'br' ? "default" : "outline"}
                  onClick={() => onNavigateQuadrant('br')}
                  className="text-xs"
                >
                  Bas-D
                </Button>
              </div>

              {/* Bouton pour marquer PCB défaillant en rouge */}
              <Button
                size="lg"
                variant={isCurrentZoneRed ? "destructive" : "outline"}
                className="w-full"
                onClick={onToggleRedZone}
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                {isCurrentZoneRed ? "Zone marquée défaillante" : "Marquer PCB défaillant"}
              </Button>
            </div>
          </>
        )}

        {/* Instructions */}
        <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
          <p><strong>Instructions:</strong></p>
          <p>• Utilisez les flèches pour naviguer entre panneaux</p>
          <p>• Touchez ⚡ pour identifier un panneau en bleu</p>
          {subdivisionMode && (
            <p>• En mode PCB, sélectionnez un quadrant puis marquez-le défaillant</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
